"use client"
import { authClient } from "@/lib/auth-client"
import { SignInEmailPasswordSchema } from "@/server/schemas/auth.schema"
import { faEye, faEyeSlash, faSignInAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Card, CardBody, CardHeader, Divider, Input, Link } from "@heroui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslations } from "next-intl"
import { useRouter, useSearchParams } from "next/navigation"
import { useState } from 'react'
import { Controller, useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { z } from "zod"

type FormType = z.infer<typeof SignInEmailPasswordSchema>
const defaultValues: FormType = {
  email: "",
  password: ""
}

export default function SignInPage() {
  const t = useTranslations("SignIn")
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectTo = searchParams.get("redirectTo") ?? "/"
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const {
    handleSubmit,
    formState: { errors },
    control
  } = useForm<FormType>({
    resolver: zodResolver(SignInEmailPasswordSchema),
    defaultValues
  })

  const onSubmit = async (data: FormType) => {
    setLoading(true)
    await authClient.signIn.email(data, {
      async onSuccess(context) {
        if (context.data.twoFactorRedirect) {
          router.replace(`/auth/verify-2fa?redirectTo=${encodeURIComponent(redirectTo)}`)
          return
        }
        router.replace(redirectTo)
      },
      async onError() {
        toast.error(t("error"))
        setLoading(false)
      }
    })
  }

  return (
    <div className="flex h-full w-full items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-none shadow-2xl bg-content1/95 backdrop-blur-md">
          <CardHeader className="flex flex-col gap-1 items-center pb-6 pt-8">
            <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <FontAwesomeIcon icon={faSignInAlt} className="text-2xl text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">{t("title")}</h1>
            <p className="text-small text-default-500">Welcome back to Nexum Space VIP</p>
          </CardHeader>

          <Divider />

          <CardBody className="px-8 py-6">
            <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => {
                  return (
                    <Input
                      label={t("email")}
                      variant="bordered"
                      placeholder="Enter your email"
                      {...field}
                      isInvalid={!!errors.email}
                      color={!!errors.email ? "danger" : "default"}
                      errorMessage={errors.email?.message}
                      classNames={{
                        input: "text-sm",
                        label: "text-sm font-medium"
                      }}
                    />
                  )
                }}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => {
                  return (
                    <Input
                      label={t("password")}
                      variant="bordered"
                      placeholder="Enter your password"
                      type={showPassword ? "text" : "password"}
                      endContent={
                        <button
                          className="focus:outline-none"
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                            className="text-default-400 text-sm hover:text-default-600"
                          />
                        </button>
                      }
                      {...field}
                      isInvalid={!!errors.password}
                      color={!!errors.password ? "danger" : "default"}
                      errorMessage={errors.password?.message}
                      classNames={{
                        input: "text-sm",
                        label: "text-sm font-medium"
                      }}
                    />
                  )
                }}
              />

              <div className="flex justify-end">
                <Link
                  size="sm"
                  className="text-primary hover:text-primary-600"
                  href="/auth/forgot-password"
                >
                  <span>{t("forgotPassword")}</span>
                </Link>
              </div>

              <Button
                type="submit"
                color="primary"
                size="lg"
                className="w-full font-semibold"
                isLoading={loading}
                startContent={!loading && <FontAwesomeIcon icon={faSignInAlt} />}
              >
                <span>{t("signIn")}</span>
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </div >
  )
}
